<template>
  <div class="admin-subscription px-2">
    <div class="admin-header-row mb-3">
      <h4 class="text-white mb-0 d-none d-lg-block">订阅模块定价管理</h4>
      <div class="admin-settings-box bg-dark p-2 rounded border border-secondary mt-2 mt-md-0">
        <div class="admin-settings-item mr-4 border-right border-secondary pr-4">
          <span class="text-white mr-3 d-none d-sm-inline">分享奖励积分:</span>
          <span class="text-white mr-1 d-inline d-sm-none" style="font-size: 0.8rem;">分享奖励:</span>
          <b-form-input 
            v-model="referralRewardPoints" 
            type="number" 
            size="sm" 
            class="admin-settings-input"
            @blur="updateSettings"
            @keyup.enter="updateSettings"
          ></b-form-input>
        </div>
        <div class="admin-settings-item mt-2 mt-sm-0">
          <span class="text-white mr-3 d-none d-sm-inline">每日名额限制:</span>
          <span class="text-white mr-1 d-inline d-sm-none" style="font-size: 0.8rem;">限额:</span>
          <b-form-checkbox v-model="isDailyLimitReached" switch @change="updateSettings" class="align-middle">
            <span :class="[isDailyLimitReached ? 'text-danger' : 'text-success', 'small']">
              <span class="d-none d-sm-inline">{{ isDailyLimitReached ? '名额已满' : '名额充足' }}</span>
              <span class="d-inline d-sm-none" style="font-size: 0.75rem;">{{ isDailyLimitReached ? '已满' : '充足' }}</span>
            </span>
          </b-form-checkbox>
        </div>
      </div>
    </div>
    
    <b-tabs content-class="mt-2" active-nav-item-class="font-weight-bold text-primary" class="admin-custom-tabs">
      <!-- 模块管理 -->
      <b-tab active>
        <template #title>
          <span class="d-none d-md-inline">模块定价管理</span>
          <span class="d-inline d-md-none">定价</span>
        </template>
        <div v-if="loading" class="text-center py-5">
          <b-spinner variant="light"></b-spinner>
        </div>
        
        <template v-else>
          <!-- PC端表格视图 -->
          <div class="d-none d-lg-block">
            <div class="table-responsive">
              <table class="table table-dark table-hover border rounded">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>模块名称</th>
                    <th>模块代码 (Code)</th>
                    <th>单月原价 (¥)</th>
                    <th>状态</th>
                    <th>描述</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="mod in modules" :key="mod.id">
                    <td>{{ mod.id }}</td>
                    <td>{{ mod.name }}</td>
                    <td><code>{{ mod.code }}</code></td>
                    <td>
                      <b-form-input 
                        v-if="editingId === mod.id" 
                        v-model="editForm.price_per_month" 
                        type="number" 
                        size="sm" 
                        style="width: 100px;" 
                        step="0.01"
                      ></b-form-input>
                      <span v-else>{{ Number(mod.price_per_month).toFixed(2) }}</span>
                    </td>
                    <td>
                      <b-form-checkbox 
                        v-if="editingId === mod.id" 
                        v-model="editForm.status" 
                        :value="1" 
                        :unchecked-value="0" 
                        switch
                      >
                        {{ editForm.status === 1 ? '需订阅' : '免费' }}
                      </b-form-checkbox>
                      <span v-else :class="mod.status === 1 ? 'text-success' : 'text-danger'">
                        {{ mod.status === 1 ? '需订阅' : '免费' }}
                      </span>
                    </td>
                    <td>
                      <span class="text-muted" style="font-size: 0.85rem;">{{ mod.description }}</span>
                    </td>
                    <td>
                      <template v-if="editingId === mod.id">
                        <b-button size="sm" variant="success" class="mr-2" @click="saveModule(mod.id)" :disabled="saving">保存</b-button>
                        <b-button size="sm" variant="secondary" @click="cancelEdit">取消</b-button>
                      </template>
                      <b-button v-else size="sm" variant="outline-primary" @click="startEdit(mod)">编辑</b-button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- 移动端卡片视图 -->
          <div class="d-lg-none module-mobile-list p-2">
            <div v-for="mod in modules" :key="mod.id" class="module-mobile-card mb-2 p-2 bg-dark rounded border border-secondary shadow-sm">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <span class="text-white font-weight-bold" style="font-size: 0.9rem;">{{ mod.name }}</span>
                <code>{{ mod.code }}</code>
              </div>
              
              <div class="d-flex justify-content-between align-items-center">
                <div style="font-size: 0.8rem;">
                  <span class="text-muted mr-1">价格:</span>
                  <b-form-input 
                    v-if="editingId === mod.id" 
                    v-model="editForm.price_per_month" 
                    type="number" 
                    size="sm" 
                    style="width: 80px; display: inline-block; padding: 2px 4px; height: 24px; font-size: 0.75rem;" 
                    step="0.01"
                  ></b-form-input>
                  <span v-else class="text-success font-weight-bold">¥{{ Number(mod.price_per_month).toFixed(2) }}</span>
                  
                  <span class="text-muted ml-3 mr-1">状态:</span>
                  <b-form-checkbox 
                    v-if="editingId === mod.id" 
                    v-model="editForm.status" 
                    :value="1" 
                    :unchecked-value="0" 
                    switch
                    class="d-inline-block align-middle"
                  >
                    <span style="font-size: 0.75rem;">{{ editForm.status === 1 ? '需订阅' : '免费' }}</span>
                  </b-form-checkbox>
                  <span v-else :class="[mod.status === 1 ? 'text-success' : 'text-danger', 'small']">
                    {{ mod.status === 1 ? '需订阅' : '免费' }}
                  </span>
                </div>
                
                <div class="actions">
                  <template v-if="editingId === mod.id">
                    <b-button size="sm" variant="success" class="py-0 px-2 mr-1" style="font-size: 0.75rem;" @click="saveModule(mod.id)" :disabled="saving">保存</b-button>
                    <b-button size="sm" variant="secondary" class="py-0 px-2" style="font-size: 0.75rem;" @click="cancelEdit">取消</b-button>
                  </template>
                  <b-button v-else size="sm" variant="outline-primary" class="py-0 px-2" style="font-size: 0.75rem;" @click="startEdit(mod)">编辑</b-button>
                </div>
              </div>
              
              <div class="text-muted small mt-1 border-top border-secondary pt-1" style="font-size: 0.75rem;">
                {{ mod.description }}
              </div>
            </div>
          </div>
        </template>
      </b-tab>

    <!-- 优惠券管理 -->
    <b-tab>
      <template #title>
        <span class="d-none d-md-inline">优惠券管理</span>
        <span class="d-inline d-md-none">优惠券</span>
      </template>
      <div class="mt-2 mt-md-3">
        <div class="admin-header-row mb-3">
          <h4 class="text-white mb-0 d-none d-lg-block">优惠券生成与管理</h4>
          <b-button variant="primary" class="mt-2 mt-md-0" @click="$bvModal.show('modal-generate-coupon')">生成优惠券</b-button>
        </div>

        <!-- PC端表格视图 -->
        <div class="d-none d-lg-block">
          <div class="table-responsive">
            <table class="table table-dark table-hover border rounded">
              <thead>
                <tr>
                  <th>券码</th>
                  <th>面额 (¥)</th>
                  <th>状态</th>
                  <th>过期时间</th>
                  <th>使用时间</th>
                  <th>关联订单</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="coupon in coupons" :key="coupon.id">
                  <td><code>{{ coupon.code }}</code></td>
                  <td class="text-success font-weight-bold">{{ Number(coupon.amount).toFixed(2) }}</td>
                  <td>
                    <b-badge :variant="coupon.status === 1 ? 'success' : 'secondary'">
                      {{ coupon.status === 1 ? '可用' : '已失效' }}
                    </b-badge>
                  </td>
                  <td>{{ coupon.expire_at }}</td>
                  <td>{{ coupon.used_at || '-' }}</td>
                  <td>{{ coupon.used_by_order || '-' }}</td>
                </tr>
                <tr v-if="coupons.length === 0">
                  <td colspan="6" class="text-center py-4 text-muted">暂无优惠券记录</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- 移动端卡片视图 -->
        <div class="d-lg-none coupon-mobile-list p-2">
          <div v-for="coupon in coupons" :key="coupon.id" class="coupon-mobile-card mb-2 p-2 bg-dark rounded border border-secondary shadow-sm">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <code>{{ coupon.code }}</code>
              <b-badge :variant="coupon.status === 1 ? 'success' : 'secondary'" class="py-0 px-1" style="font-size: 0.75rem;">
                {{ coupon.status === 1 ? '可用' : '已失效' }}
              </b-badge>
            </div>
            <div class="d-flex justify-content-between text-muted small flex-wrap" style="font-size: 0.8rem;">
              <div>💰 面额: <span class="text-success font-weight-bold">¥{{ Number(coupon.amount).toFixed(2) }}</span></div>
              <div>📅 过期: {{ coupon.expire_at ? coupon.expire_at.substring(2, 10) : '-' }}</div>
            </div>
            <div class="d-flex justify-content-between text-muted small mt-1 pt-1 border-top border-secondary" style="font-size: 0.75rem;">
              <div>用时: {{ coupon.used_at || '-' }}</div>
              <div>订单: {{ coupon.used_by_order || '-' }}</div>
            </div>
          </div>
          <div v-if="coupons.length === 0" class="text-center py-4 text-muted bg-dark rounded border border-secondary">暂无优惠券记录</div>
        </div>
      </div>
    </b-tab>

    <!-- 用户订阅列表 -->
    <b-tab>
      <template #title>
        <span class="d-none d-md-inline">用户订阅列表</span>
        <span class="d-inline d-md-none">订阅</span>
      </template>
      <div class="mt-2 mt-md-3">
        <div class="admin-header-row mb-3">
          <h4 class="text-white mb-0 d-none d-lg-block">用户订阅状态一览</h4>
          <b-button variant="outline-light" size="sm" class="mt-2 mt-md-0" @click="fetchUserList">刷新列表</b-button>
        </div>

        <!-- PC端表格视图 -->
        <div class="d-none d-lg-block">
          <div class="table-responsive">
            <table class="table table-dark table-hover border rounded">
              <thead>
                <tr>
                  <th>用户</th>
                  <th>订阅内容</th>
                  <th @click="toggleSort('start_time')" style="cursor: pointer;" title="点击排序">
                    开始时间
                    <b-icon v-if="sortBy === 'start_time'" :icon="sortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" font-scale="0.8"></b-icon>
                  </th>
                  <th @click="toggleSort('end_time')" style="cursor: pointer;" title="点击排序">
                    结束时间
                    <b-icon v-if="sortBy === 'end_time'" :icon="sortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" font-scale="0.8"></b-icon>
                  </th>
                  <th @click="toggleSort('created_at')" style="cursor: pointer;" title="点击排序">
                    创建时间
                    <b-icon v-if="sortBy === 'created_at'" :icon="sortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" font-scale="0.8"></b-icon>
                  </th>
                  <th @click="toggleSort('updated_at')" style="cursor: pointer;" title="点击排序">
                    更新时间
                    <b-icon v-if="sortBy === 'updated_at'" :icon="sortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" font-scale="0.8"></b-icon>
                  </th>
                  <th>状态</th>
                  <th>最近支付</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="sub in userList" :key="sub.id">
                  <td>
                    <div class="text-white">{{ sub.user_nick }}</div>
                    <small class="text-muted">@{{ sub.user_name }} (UID: {{ sub.user_id }})</small>
                  </td>
                  <td>
                    <b-badge variant="info">{{ sub.module_name }}</b-badge>
                    <div class="mt-1"><small class="text-muted"><code>{{ sub.module_code }}</code></small></div>
                  </td>
                  <td><small>{{ sub.start_time }}</small></td>
                  <td>
                    <div :class="isExpired(sub.end_time) ? 'text-danger' : 'text-success'">
                      {{ sub.end_time }}
                    </div>
                  </td>
                  <td><small class="text-muted">{{ sub.created_at || '-' }}</small></td>
                  <td><small class="text-muted">{{ sub.updated_at || '-' }}</small></td>
                  <td>
                    <b-badge :variant="sub.status === 1 ? 'success' : 'secondary'">
                      {{ sub.status === 1 ? '有效' : '禁用' }}
                    </b-badge>
                  </td>
                  <td><small class="text-success">{{ formatRecentPayment(sub.total_amount, sub.original_amount) }}</small></td>
                </tr>
                <tr v-if="userList.length === 0">
                  <td colspan="8" class="text-center py-4 text-muted">暂无订阅记录</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- 移动端卡片视图 -->
        <div class="d-lg-none userlist-mobile-list p-2">
          <div v-for="sub in userList" :key="sub.id" class="userlist-mobile-card mb-2 p-2 bg-dark rounded border border-secondary shadow-sm">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <div>
                <span class="text-white font-weight-bold" style="font-size: 0.85rem;">{{ sub.user_nick }}</span>
                <small class="text-muted ml-1">@{{ sub.user_name }} ({{ sub.user_id }})</small>
              </div>
              <b-badge :variant="sub.status === 1 ? 'success' : 'secondary'" class="py-0 px-1" style="font-size: 0.75rem;">
                {{ sub.status === 1 ? '有效' : '禁用' }}
              </b-badge>
            </div>
            
            <div class="d-flex justify-content-between align-items-center mb-1 flex-wrap" style="font-size: 0.8rem;">
              <div>
                <span class="text-muted mr-1">订阅:</span>
                <b-badge variant="info" class="py-0 px-1" style="font-size: 0.75rem;">{{ sub.module_name }}</b-badge>
                <code class="small text-muted ml-1">{{ sub.module_code }}</code>
              </div>
              <div>
                <span class="text-muted mr-1">实付/原价:</span>
                <span class="text-success font-weight-bold">{{ formatRecentPayment(sub.total_amount, sub.original_amount) }}</span>
              </div>
            </div>
            
            <div class="d-flex justify-content-between text-muted small mt-1 pt-1 border-top border-secondary" style="font-size: 0.75rem;">
              <div>始: {{ sub.start_time ? sub.start_time.substring(2, 10) : '-' }}</div>
              <div>终: <span :class="isExpired(sub.end_time) ? 'text-danger' : 'text-success'">{{ sub.end_time ? sub.end_time.substring(2, 10) : '-' }}</span></div>
              <div>创: {{ sub.created_at ? sub.created_at.substring(2, 10) : '-' }}</div>
            </div>
          </div>
          <div v-if="userList.length === 0" class="text-center py-4 text-muted bg-dark rounded border border-secondary">暂无订阅记录</div>
        </div>
        <div v-if="totalRows > perPage" class="mt-4 d-flex justify-content-center">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            align="center"
            @change="handlePageChange"
          ></b-pagination>
        </div>
      </div>
    </b-tab>
    <!-- 微信通知管理 -->
    <b-tab>
      <template #title>
        <span class="d-none d-md-inline">微信通知</span>
        <span class="d-inline d-md-none">微信</span>
      </template>
      <div class="mt-2 mt-md-3">
        <div class="admin-header-row mb-3">
          <h4 class="text-white mb-0 d-none d-lg-block">微信通知用户管理</h4>
          <div class="admin-header-actions mt-2 mt-md-0">
            <b-form-input
              v-model="quantSearchCode"
              placeholder="输入 quant_code 查询 (默认 9178)"
              size="sm"
              class="search-input mr-2 mb-2 mb-sm-0"
              style="width: 250px;"
              @keyup.enter="fetchQuantUsers"
            ></b-form-input>
            <div class="button-group">
              <b-button variant="outline-primary" size="sm" class="mr-2" @click="fetchQuantUsers">查询</b-button>
              <b-button variant="primary" size="sm" @click="startAddQuantUser">新增记录</b-button>
            </div>
          </div>
        </div>

        <!-- PC端表格视图 -->
        <div class="d-none d-lg-block">
          <div class="table-responsive">
            <table class="table table-dark table-hover border rounded">
              <thead>
                <tr>
                  <th>序号</th>
                  <th>ID</th>
                  <th>Quant Code</th>
                  <th>Group</th>
                  <th>Quant Name</th>
                  <th>有效开始日期</th>
                  <th @click="toggleQuantSort('valid_to_date')" style="cursor: pointer;" title="点击排序">
                    有效结束日期
                    <b-icon v-if="quantSortBy === 'valid_to_date'" :icon="quantSortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" font-scale="0.8"></b-icon>
                  </th>
                  <th>微信名称</th>
                  <th>Open ID</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(qu, index) in quantUsers" :key="qu.id">
                  <td>{{ (quantCurrentPage - 1) * quantPerPage + index + 1 }}</td>
                  <td>{{ qu.id }}</td>
                  <td>{{ qu.quant_code }}</td>
                  <td>{{ qu.group }}</td>
                  <td>{{ qu.quant_name || '-' }}</td>
                  <td>{{ qu.valid_from_date || '-' }}</td>
                  <td :class="isExpired(qu.valid_to_date) ? 'text-danger' : ''">{{ qu.valid_to_date || '-' }}</td>
                  <td>{{ qu.wx_name || '-' }}</td>
                  <td><small><code>{{ qu.wx_open_id || '-' }}</code></small></td>
                  <td>
                    <b-button size="sm" variant="outline-primary" @click="startEditQuantUser(qu)">编辑</b-button>
                  </td>
                </tr>
                <tr v-if="quantUsers.length === 0">
                  <td colspan="10" class="text-center py-4 text-muted">暂无微信通知记录</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- 移动端卡片视图 -->
        <div class="d-lg-none quant-mobile-list p-2">
          <div v-for="qu in quantUsers" :key="qu.id" class="quant-mobile-card mb-2 p-2 bg-dark rounded border border-secondary shadow-sm">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <div class="d-flex align-items-center">
                <span class="text-white font-weight-bold mr-2" style="font-size: 0.95rem;">#{{ qu.id }}</span>
                <code class="mr-2" style="font-size: 0.8rem;">Code: {{ qu.quant_code }}</code>
                <span class="text-muted small">Group: {{ qu.group }}</span>
              </div>
              <b-button size="sm" variant="outline-primary" class="py-0 px-2" style="font-size: 0.75rem;" @click="startEditQuantUser(qu)">编辑</b-button>
            </div>
            <div class="d-flex justify-content-between text-muted small mb-1" style="font-size: 0.8rem;">
              <div>策略: <span class="text-white small font-weight-bold">{{ qu.quant_name || '-' }}</span></div>
              <div>微信: <span class="text-white small font-weight-bold">{{ qu.wx_name || '-' }}</span></div>
            </div>
            <div class="d-flex justify-content-between text-muted small mt-1 pt-1 border-top border-secondary" style="font-size: 0.75rem;">
              <div>始: {{ qu.valid_from_date || '-' }}</div>
              <div>终: <span :class="isExpired(qu.valid_to_date) ? 'text-danger' : ''">{{ qu.valid_to_date || '-' }}</span></div>
              <div>OpenID: <code class="text-muted">{{ qu.wx_open_id ? qu.wx_open_id.substring(0, 8) + '...' : '-' }}</code></div>
            </div>
          </div>
          <div v-if="quantUsers.length === 0" class="text-center py-4 text-muted bg-dark rounded border border-secondary">暂无微信通知记录</div>
        </div>
        <div v-if="quantTotalRows > quantPerPage" class="mt-4 d-flex justify-content-center">
          <b-pagination
            v-model="quantCurrentPage"
            :total-rows="quantTotalRows"
            :per-page="quantPerPage"
            align="center"
            @change="handleQuantPageChange"
          ></b-pagination>
        </div>
      </div>
    </b-tab>
  </b-tabs>

    <!-- 生成优惠券弹窗 -->
    <b-modal id="modal-generate-coupon" title="批量生成优惠券" header-bg-variant="dark" header-text-variant="light" body-bg-variant="dark" body-text-variant="light" footer-bg-variant="dark" footer-text-variant="light" @ok="handleGenerateCoupons">
      <b-form>
        <b-form-group label="抵扣面额 (¥)" label-for="coupon-amount">
          <b-form-input id="coupon-amount" v-model="genForm.amount" type="number" placeholder="输入抵扣金额"></b-form-input>
        </b-form-group>
        <b-form-group label="生成数量" label-for="coupon-count">
          <b-form-input id="coupon-count" v-model="genForm.count" type="number" placeholder="输入生成个数"></b-form-input>
        </b-form-group>
        <b-form-group label="券码前缀" label-for="coupon-prefix">
          <b-form-input id="coupon-prefix" v-model="genForm.prefix" placeholder="例如: OFF, VIP"></b-form-input>
        </b-form-group>
        <b-form-group label="有效期 (天)" label-for="coupon-days">
          <b-form-input id="coupon-days" v-model="genForm.expire_days" type="number" placeholder="输入过期天数"></b-form-input>
        </b-form-group>
      </b-form>
    </b-modal>

    <!-- 微信通知管理弹窗 -->
    <b-modal id="modal-quant-user" :title="quantForm.id ? '编辑微信通知' : '新增微信通知'" header-bg-variant="dark" header-text-variant="light" body-bg-variant="dark" body-text-variant="light" footer-bg-variant="dark" footer-text-variant="light" @ok="handleSaveQuantUser">
      <b-form>
        <b-form-group label="Quant Code" label-for="qu-quant-code">
          <b-form-input id="qu-quant-code" v-model="quantForm.quant_code" type="number" placeholder="例如: 9178"></b-form-input>
        </b-form-group>
        <b-form-group label="Group" label-for="qu-group">
          <b-form-input id="qu-group" v-model="quantForm.group" type="number" placeholder="输入组别 (例如: 0)"></b-form-input>
        </b-form-group>
        <b-form-group label="Quant Name" label-for="qu-quant-name">
          <b-form-input id="qu-quant-name" v-model="quantForm.quant_name" placeholder="例如: 某某策略"></b-form-input>
        </b-form-group>
        <b-form-group label="有效开始日期" label-for="qu-valid-from">
          <b-form-input id="qu-valid-from" v-model="quantForm.valid_from_date" type="date" placeholder="YYYY-MM-DD"></b-form-input>
        </b-form-group>
        <b-form-group label="有效结束日期" label-for="qu-valid-to">
          <b-form-input id="qu-valid-to" v-model="quantForm.valid_to_date" type="date" placeholder="YYYY-MM-DD"></b-form-input>
        </b-form-group>
        <b-form-group label="微信名称" label-for="qu-wx-name">
          <b-form-input id="qu-wx-name" v-model="quantForm.wx_name" placeholder="微信昵称"></b-form-input>
        </b-form-group>
        <b-form-group label="Open ID" label-for="qu-wx-openid">
          <b-form-input id="qu-wx-openid" v-model="quantForm.wx_open_id" placeholder="微信 Open ID"></b-form-input>
        </b-form-group>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import { request_zzquant } from 'network/request.js'

export default {
  name: 'AdminSubscription',
  data() {
    return {
      modules: [],
      loading: true,
      saving: false,
      editingId: null,
      editForm: {
        price_per_month: 0,
        status: 1
      },
      coupons: [],
      genForm: {
        amount: 100,
        count: 1,
        prefix: 'OFF',
        expire_days: 30
      },
      isDailyLimitReached: false,
      referralRewardPoints: 5,
      userList: [],
      currentPage: 1,
      perPage: 20,
      totalRows: 0,
      sortBy: 'updated_at',
      sortOrder: 'desc',
      
      // 微信通知
      quantUsers: [],
      quantSearchCode: 9178,
      quantCurrentPage: 1,
      quantPerPage: 50,
      quantTotalRows: 0,
      quantSortBy: 'id',
      quantSortOrder: 'desc',
      quantForm: {
        id: null,
        quant_code: 9178,
        group: 0,
        quant_name: '',
        valid_from_date: '',
        valid_to_date: '',
        wx_open_id: '',
        wx_name: ''
      }
    }
  },
  created() {
    this.fetchModules()
    this.fetchCoupons()
    this.fetchSettings()
    this.fetchUserList()
    this.fetchQuantUsers()
  },
  methods: {
    fetchModules() {
      this.loading = true
      request_zzquant({
        url: '/v3/admin/subscription/modules',
        method: 'GET'
      })
      .then(res => {
        if (res.data && res.data.code === 20000) {
          this.modules = res.data.data || []
        }
      })
      .catch(() => {
        this.$bvToast.toast('加载模块失败', { variant: 'danger', solid: true })
      })
      .finally(() => {
        this.loading = false
      })
    },
    startEdit(mod) {
      this.editingId = mod.id
      this.editForm = {
        price_per_month: Number(mod.price_per_month),
        status: mod.status
      }
    },
    cancelEdit() {
      this.editingId = null
    },
    saveModule(id) {
      this.saving = true
      request_zzquant({
        url: `/v3/admin/subscription/modules/${id}`,
        method: 'PUT',
        data: {
          price_per_month: Number(this.editForm.price_per_month),
          status: this.editForm.status
        }
      })
      .then(res => {
        if (res.data && res.data.code === 20000) {
          this.$bvToast.toast('保存成功', { variant: 'success', solid: true })
          this.editingId = null
          this.fetchModules()
        } else {
          this.$bvToast.toast((res.data && res.data.message) || '保存失败', { variant: 'danger', solid: true })
        }
      })
      .catch(() => {
        this.$bvToast.toast('保存请求失败', { variant: 'danger', solid: true })
      })
      .finally(() => {
        this.saving = false
      })
    },
    fetchCoupons() {
      request_zzquant({
        url: '/v3/admin/subscription/coupons',
        method: 'GET'
      })
      .then(res => {
        if (res.data && res.data.code === 20000) {
          this.coupons = res.data.data || []
        }
      })
    },
    handleGenerateCoupons(bvModalEvt) {
      // 防止弹窗自动关闭，等待异步完成
      bvModalEvt.preventDefault()
      
      if (!this.genForm.amount || !this.genForm.count) {
        this.$bvToast.toast('请填写金额和数量', { variant: 'warning', solid: true })
        return
      }

      request_zzquant({
        url: '/v3/admin/subscription/coupons/generate',
        method: 'POST',
        data: {
          amount: Number(this.genForm.amount),
          count: Number(this.genForm.count),
          prefix: this.genForm.prefix,
          expire_days: Number(this.genForm.expire_days)
        }
      })
      .then(res => {
        if (res.data && res.data.code === 20000) {
          this.$bvToast.toast(`成功生成 ${res.data.data.length} 张优惠券`, { variant: 'success', solid: true })
          this.$bvModal.hide('modal-generate-coupon')
          this.fetchCoupons()
        } else {
          this.$bvToast.toast(res.data.message || '生成失败', { variant: 'danger', solid: true })
        }
      })
      .catch(() => {
        this.$bvToast.toast('生成请求失败', { variant: 'danger', solid: true })
      })
    },
    fetchSettings() {
      request_zzquant({
        url: '/v3/admin/subscription/settings',
        method: 'GET'
      })
      .then(res => {
        if (res.data && res.data.code === 20000) {
          this.isDailyLimitReached = res.data.data.is_daily_limit_reached
          this.referralRewardPoints = res.data.data.referral_reward_points
        }
      })
    },
    updateSettings() {
      request_zzquant({
        url: '/v3/admin/subscription/settings',
        method: 'POST',
        data: {
          is_daily_limit_reached: this.isDailyLimitReached,
          referral_reward_points: Number(this.referralRewardPoints)
        }
      })
      .then(res => {
        if (res.data && res.data.code === 20000) {
          const msg = this.isDailyLimitReached ? '已开启名额限制' : '已解除名额限制'
          this.$bvToast.toast(msg, { variant: 'success', solid: true })
        } else {
          this.$bvToast.toast('更新设置失败', { variant: 'danger', solid: true })
          // 恢复原状
          this.isDailyLimitReached = !this.isDailyLimitReached
        }
      })
      .catch(() => {
        this.$bvToast.toast('网络请求失败', { variant: 'danger', solid: true })
        this.isDailyLimitReached = !this.isDailyLimitReached
      })
    },
    fetchUserList() {
      request_zzquant({
        url: '/v3/admin/subscription/user-list',
        method: 'GET',
        params: {
          page: this.currentPage,
          page_size: this.perPage,
          sort_by: this.sortBy,
          sort_order: this.sortOrder
        }
      })
      .then(res => {
        if (res.data && res.data.code === 20000) {
          const { items, total } = res.data.data
          this.userList = items || []
          this.totalRows = total || 0
        }
      })
    },
    handlePageChange(page) {
      this.currentPage = page
      this.$nextTick(() => {
        this.fetchUserList()
      })
    },
    toggleSort(key) {
      if (this.sortBy === key) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortBy = key
        this.sortOrder = 'desc'
      }
      this.currentPage = 1
      this.fetchUserList()
    },
    isExpired(endTime) {
      if (!endTime || endTime === '-') return false
      return new Date(endTime) < new Date()
    },
    formatAmount(amount) {
      if (amount === null || amount === undefined || amount === '') return '-'
      const value = Number(amount)
      return Number.isNaN(value) ? '-' : `¥${value.toFixed(2)}`
    },
    formatRecentPayment(totalAmount, originalAmount) {
      const paid = this.formatAmount(totalAmount)
      const original = this.formatAmount(originalAmount)
      if (paid === '-' && original === '-') return '-'
      return `${paid}/${original}`
    },
    
    // 微信通知管理
    fetchQuantUsers() {
      request_zzquant({
        url: '/v3/admin/subscription/quant-users',
        method: 'GET',
        params: {
          quant_code: this.quantSearchCode,
          page: this.quantCurrentPage,
          page_size: this.quantPerPage,
          sort_by: this.quantSortBy,
          sort_order: this.quantSortOrder
        }
      })
      .then(res => {
        if (res.data && res.data.code === 20000) {
          const { items, total } = res.data.data
          this.quantUsers = items || []
          this.quantTotalRows = total || 0
        }
      })
    },
    handleQuantPageChange(page) {
      this.quantCurrentPage = page
      this.$nextTick(() => {
        this.fetchQuantUsers()
      })
    },
    toggleQuantSort(key) {
      if (this.quantSortBy === key) {
        this.quantSortOrder = this.quantSortOrder === 'asc' ? 'desc' : 'asc'
      } else {
        this.quantSortBy = key
        this.quantSortOrder = 'desc'
      }
      this.quantCurrentPage = 1
      this.fetchQuantUsers()
    },
    startAddQuantUser() {
      this.quantForm = {
        id: null,
        quant_code: this.quantSearchCode || 9178,
        group: 0,
        quant_name: '',
        valid_from_date: '',
        valid_to_date: '',
        wx_open_id: '',
        wx_name: ''
      }
      this.$bvModal.show('modal-quant-user')
    },
    startEditQuantUser(qu) {
      this.quantForm = { ...qu }
      this.$bvModal.show('modal-quant-user')
    },
    handleSaveQuantUser(bvModalEvt) {
      bvModalEvt.preventDefault()
      
      if (!this.quantForm.quant_code) {
        this.$bvToast.toast('请填写 Quant Code', { variant: 'warning', solid: true })
        return
      }

      const isEdit = !!this.quantForm.id
      const url = isEdit 
        ? `/v3/admin/subscription/quant-users/${this.quantForm.id}`
        : `/v3/admin/subscription/quant-users`
      const method = isEdit ? 'PUT' : 'POST'
      
      const payload = {
        quant_code: Number(this.quantForm.quant_code),
        group: Number(this.quantForm.group || 0),
        quant_name: this.quantForm.quant_name,
        valid_from_date: this.quantForm.valid_from_date || null,
        valid_to_date: this.quantForm.valid_to_date || null,
        wx_open_id: this.quantForm.wx_open_id,
        wx_name: this.quantForm.wx_name
      }

      request_zzquant({ url, method, data: payload })
      .then(res => {
        if (res.data && res.data.code === 20000) {
          this.$bvToast.toast(isEdit ? '修改成功' : '新增成功', { variant: 'success', solid: true })
          this.$bvModal.hide('modal-quant-user')
          this.fetchQuantUsers()
        } else {
          this.$bvToast.toast(res.data.message || '保存失败', { variant: 'danger', solid: true })
        }
      })
      .catch(() => {
        this.$bvToast.toast('网络请求失败', { variant: 'danger', solid: true })
      })
    }
  }
}
</script>

<style scoped>
.admin-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
}

.admin-settings-box {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.admin-settings-item {
  display: flex;
  align-items: center;
}

.admin-settings-input {
  width: 80px;
}

.admin-header-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.button-group {
  display: flex;
  align-items: center;
}

/* 深度选择器样式，美化并压缩导航标签 */
.admin-custom-tabs >>> .nav-tabs {
  border-bottom: 1px solid #2d3d51;
  margin-bottom: 8px !important;
}
.admin-custom-tabs >>> .nav-tabs .nav-link {
  color: #94a3b8;
  border: none;
  background: transparent;
  padding: 8px 16px;
  font-size: 0.95rem;
  transition: all 0.2s;
}
.admin-custom-tabs >>> .nav-tabs .nav-link:hover {
  color: #fff;
}
.admin-custom-tabs >>> .nav-tabs .nav-link.active {
  color: var(--accent-color) !important;
  border-bottom: 2px solid var(--accent-color) !important;
  background: transparent;
}

/* 手机端及平板适配，对齐 991px */
@media (max-width: 991px) {
  .admin-header-row {
    flex-direction: row !important;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px !important;
  }
  
  .admin-header-row > h4 {
    margin-bottom: 0 !important;
  }
  
  .admin-settings-box {
    flex-direction: row !important;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 6px 8px !important;
    margin-top: 0 !important;
  }
  
  .admin-settings-item {
    flex: 1;
    justify-content: center !important;
    width: auto !important;
    margin-right: 0 !important;
    padding-right: 0 !important;
    border-right: none !important;
  }
  
  .admin-settings-item:first-child {
    border-bottom: none !important;
    padding-bottom: 0 !important;
    border-right: 1px solid #334155 !important;
    padding-right: 8px !important;
    margin-right: 8px !important;
  }
  
  .admin-settings-input {
    width: 55px !important;
    height: 24px !important;
    font-size: 0.75rem !important;
    padding: 2px 4px !important;
    background-color: #1e293b !important;
    color: #fff !important;
    border-color: #475569 !important;
  }
  
  .admin-header-actions {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    gap: 8px;
  }
  
  .search-input {
    width: 100% !important;
    margin-right: 0 !important;
  }
  
  .button-group {
    width: 100%;
    justify-content: stretch;
  }
  
  .button-group > button,
  .admin-header-row > button {
    flex: 1;
    margin-top: 4px;
  }
  
  .admin-custom-tabs >>> .nav-tabs {
    display: flex !important;
    justify-content: space-around !important;
  }
  
  .admin-custom-tabs >>> .nav-tabs .nav-link {
    padding: 6px 2px !important;
    font-size: 0.8rem !important;
    flex: 1;
    text-align: center;
  }
}

.module-mobile-card,
.coupon-mobile-card,
.userlist-mobile-card,
.quant-mobile-card {
  transition: transform 0.2s, box-shadow 0.2s;
}
.module-mobile-card:hover,
.coupon-mobile-card:hover,
.userlist-mobile-card:hover,
.quant-mobile-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4) !important;
}
</style>
